/**
 * Feature Flag Management
 * Handles reading feature flags from query strings and applying defaults
 */

/**
 * Parse query string parameters
 * @returns {Object} Object with key-value pairs from query string
 */
const parseQueryString = () => {
  return Object.fromEntries(new URLSearchParams(window.location.search));
};

/**
 * Convert string values to boolean
 * @param {string} value - The string value to convert
 * @returns {boolean|null} Boolean value or null if not a valid boolean string
 */
const stringToBoolean = (value) => {
  if (value === undefined || value === null) return null;
  const lowerValue = String(value).toLowerCase();
  if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'on' || lowerValue === 'yes') {
    return true;
  }
  if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'off' || lowerValue === 'no') {
    return false;
  }
  return null;
};

/**
 * Get feature flag value from query string
 * @param {string} flagName - The name of the feature flag
 * @param {*} defaultValue - The default value if flag is not set
 * @returns {*} The feature flag value
 */
export const getFeatureFlag = (flagName, defaultValue) => {
  try {
    const params = parseQueryString();
    const queryValue = params[flagName];
    
    if (queryValue !== undefined) {
      // Try to convert to boolean if it looks like a boolean
      const boolValue = stringToBoolean(queryValue);
      return boolValue !== null ? boolValue : queryValue;
    }
  } catch (e) {
    console.error('Error reading feature flag:', flagName, e);
  }
  
  return defaultValue;
};

/**
 * Check if the chat feature is enabled
 * @returns {boolean} True if chat is enabled
 */
export const isChatEnabled = () => {
  return getFeatureFlag('enableChat', false);
};

/**
 * Check if the filter-first search feature is enabled
 * @returns {boolean} True if filter search is enabled
 */
export const isFilterSearchEnabled = () => {
  return getFeatureFlag('enableFilterSearch', true);
};

/**
 * Check if the Cytoscape.js chart engine is enabled (replaces ReGraph)
 * @returns {boolean} True if Cytoscape engine is enabled
 */
export const isCytoscapeEnabled = () => {
  return getFeatureFlag('enableCytoscape', true) || getFeatureFlag('useCytoscape', false);
};

export default {
  getFeatureFlag,
  parseQueryString,
  isChatEnabled,
  isFilterSearchEnabled,
  isCytoscapeEnabled,
};
